import NeptuneElement from '../../../NeptuneElement.js';

/**
 * @class InputFileItem
 * @extends NeptuneElement
 *
 * Presentational component representing exactly one selected file.
 * Renders a status icon, file name, progress indicator, size/status text and a
 * remove control. It does not open a file picker, validate files, upload files,
 * maintain a file list, calculate progress, estimate remaining time, or decide
 * upload success/failure — all of that is owned by the composing parent
 * (`neptune-input-file`).
 */
class InputFileItem extends NeptuneElement {

    static STATUSES = ['in-progress', 'success', 'error'];
    static DEFAULT_STATUS = 'in-progress';
    static DEFAULT_SUCCESS_MESSAGE = 'Upload successful';
    static DEFAULT_ERROR_MESSAGE = 'Upload failed';

    static STATUS_ICONS = {
        'in-progress': 'fa-regular fa-file',
        success: 'fa-solid fa-check',
        error: 'fa-solid fa-circle-exclamation'
    };

    templateMarkup = `
        <div>
            <div id="statusIcon" aria-hidden="true">
                <i aria-hidden="true"></i>
            </div>
            <div id="details">
                <span class="font-size-md" id="fileName"></span>
                <neptune-progress id="fileProgress" value="0" max="100" type="default" thickness="small"></neptune-progress>
                <div id="meta">
                    <span class="font-size-sm" id="fileSize"></span>
                    <span class="font-size-sm" id="metaMessage"></span>
                </div>
            </div>
            <neptune-button id="removeButton" type="tertiary" size="small">
                <i class="fa-solid fa-xmark" aria-hidden="true"></i>
            </neptune-button>
        </div>
    `;

    componentCssUrl = '/src/components/form/inputFile/inputFileItem/input-file-item.css';

    #file = null;
    #removeButtonElement = null;
    #handleRemoveClick = null;

    constructor() {
        super();
    }

    /**
     * Invoked when the component is added to the DOM.
     * Applies the default status, processes any pre-set attributes, and wires listeners.
     * @returns {Promise<void>}
     */
    async connectedCallback() {
        await super.connectedCallback();
        this.processInitialAttributesAfterRender(InputFileItem.observedAttributes);
        if (!this.hasAttribute('status')) {
            this.setAttribute('status', InputFileItem.DEFAULT_STATUS);
        }
        this.#renderFileInfo();
        this.setEventListeners();
    }

    /**
     * Invoked when the component is removed from the DOM.
     * Cleans up the remove-button listener.
     * @returns {Promise<void>}
     */
    async disconnectedCallback() {
        this.removeEventListeners();
        await super.disconnectedCallback();
    }

    static get observedAttributes() {
        return ['item-id', 'status', 'progress', 'status-message', 'error-message', 'disabled'];
    }

    attributeChangedCallback(name, oldValue, newValue) {
        if (!this.isRendered) { return; }
        this.processAttribute(name, oldValue, newValue);
    }

    processAttribute(name, oldValue, newValue) {
        switch (name) {
            case 'item-id':
                this.#updateRemoveButtonAccessibleName();
                break;
            case 'status':
                this.validatePropertyValue(name, newValue, InputFileItem.STATUSES);
                this.#updateStatusIcon();
                this.#updateProgressBar();
                this.#updateMetaMessage();
                this.#updateFileSize();
                break;
            case 'progress':
                this.#updateProgressBar();
                break;
            case 'status-message':
            case 'error-message':
                this.#updateMetaMessage();
                break;
            case 'disabled': {
                const removeButton = this.shadowRoot?.querySelector('#removeButton');
                if (!removeButton) { break; }
                /* neptune-button reads its own disabled state from the attribute, not the IDL property. */
                if (this.hasAttribute('disabled')) { removeButton.setAttribute('disabled', ''); }
                else { removeButton.removeAttribute('disabled'); }
                break;
            }
            default:
                break;
        }
    }

    setEventListeners() {
        if (!this.isRendered) { return; }
        this.removeEventListeners();

        this.#removeButtonElement = this.shadowRoot?.querySelector('#removeButton');
        if (!this.#removeButtonElement) { return; }

        this.#handleRemoveClick = () => {
            if (this.disabled) { return; }
            this.dispatchEvent(new CustomEvent('file-remove', {
                detail: { id: this.itemId, file: this.file },
                bubbles: true,
                composed: true
            }));
        };
        this.#removeButtonElement.addEventListener('click', this.#handleRemoveClick);
    }

    removeEventListeners() {
        if (this.#removeButtonElement && this.#handleRemoveClick) {
            this.#removeButtonElement.removeEventListener('click', this.#handleRemoveClick);
        }
        this.#removeButtonElement = null;
        this.#handleRemoveClick = null;
    }

    get itemId() { return this.getAttribute('item-id') || ''; }
    set itemId(value) {
        if (value) { this.setAttribute('item-id', String(value)); }
        else { this.removeAttribute('item-id'); }
    }

    get file() { return this.#file; }
    set file(value) {
        this.#file = value instanceof File ? value : null;
        if (this.isRendered) { this.#renderFileInfo(); }
    }

    get status() { return this.getAttribute('status') || InputFileItem.DEFAULT_STATUS; }
    set status(value) { this.setAttribute('status', value); }

    get progress() { return this.#normalizeProgress(this.getAttribute('progress')); }
    set progress(value) { this.setAttribute('progress', String(value)); }

    get statusMessage() { return this.getAttribute('status-message') || ''; }
    set statusMessage(value) {
        if (value) { this.setAttribute('status-message', String(value)); }
        else { this.removeAttribute('status-message'); }
    }

    get errorMessage() { return this.getAttribute('error-message') || ''; }
    set errorMessage(value) {
        if (value) { this.setAttribute('error-message', String(value)); }
        else { this.removeAttribute('error-message'); }
    }

    get disabled() { return this.hasAttribute('disabled'); }
    set disabled(value) {
        if (value) { this.setAttribute('disabled', ''); }
        else { this.removeAttribute('disabled'); }
    }

    get removable() { return this.hasAttribute('removable'); }
    set removable(value) {
        if (value) { this.setAttribute('removable', ''); }
        else { this.removeAttribute('removable'); }
    }

    /**
     * Clamps a raw progress value to the 0-100 range, coercing invalid input to 0.
     * @param {*} rawValue
     * @returns {number}
     */
    #normalizeProgress(rawValue) {
        const numeric = Number(rawValue);
        if (!Number.isFinite(numeric)) { return 0; }
        return Math.min(100, Math.max(0, numeric));
    }

    /**
     * Formats a byte count as a human-readable size string. Returns '' when not available.
     * @param {*} bytes
     * @returns {string}
     */
    #formatFileSize(bytes) {
        if (typeof bytes !== 'number' || !Number.isFinite(bytes) || bytes < 0) { return ''; }

        const units = ['B', 'KB', 'MB', 'GB', 'TB'];
        let value = bytes;
        let unitIndex = 0;
        while (value >= 1024 && unitIndex < units.length - 1) {
            value /= 1024;
            unitIndex += 1;
        }

        const precision = unitIndex === 0 ? 0 : 1;
        return `${value.toFixed(precision)} ${units[unitIndex]}`;
    }

    /**
     * Swaps the single status icon's class list based on the current status.
     * @returns {void}
     */
    #updateStatusIcon() {
        const iconElement = this.shadowRoot?.querySelector('#statusIcon i');
        if (!iconElement) { return; }

        iconElement.className = InputFileItem.STATUS_ICONS[this.status];
    }

    /**
     * Renders the file name (visually truncated, fully accessible) and file size.
     * @returns {void}
     */
    #renderFileInfo() {
        const fileNameElement = this.shadowRoot?.querySelector('#fileName');
        const fileSizeElement = this.shadowRoot?.querySelector('#fileSize');
        if (!fileNameElement || !fileSizeElement) { return; }

        const name = this.#file?.name || '';
        fileNameElement.textContent = name;
        fileNameElement.title = name;

        fileSizeElement.textContent = this.#formatFileSize(this.#file?.size);

        this.#updateRemoveButtonAccessibleName();
        this.#updateProgressBar();
        this.#updateFileSize();
    }

    /**
     * Updates the neptune-progress value and type. Success always renders fully filled;
     * error maps to the progress bar's error type (no dedicated success/green type exists).
     * @returns {void}
     */
    #updateProgressBar() {
        const progressElement = this.shadowRoot?.querySelector('#fileProgress');
        if (!progressElement) { return; }

        let displayValue;
        if (this.status === 'success') {
            displayValue = 100;
        } else if (this.status === 'error') {
            displayValue = 100;
        } else {
            displayValue = this.progress;
        }

        let progressType;
        if (this.status === 'error') {
            progressType = 'error';
        } else if (this.status === 'success') {
            progressType = 'success';
        } else {
            progressType = 'default';
        }
        progressElement.setAttribute('type', progressType);
        progressElement.setAttribute('value', String(displayValue));
    }

    /**
     * Updates the size/status text region based on the current status.
     * @returns {void}
     */
    #updateMetaMessage() {
        const metaMessageElement = this.shadowRoot?.querySelector('#metaMessage');
        if (!metaMessageElement) { return; }

        let message;
        switch (this.status) {
            case 'success':
                message = this.statusMessage || InputFileItem.DEFAULT_SUCCESS_MESSAGE;
                break;
            case 'error': {
                const defaultError = InputFileItem.DEFAULT_ERROR_MESSAGE;
                const reason = this.errorMessage;
                message = reason ? `${defaultError} • ${reason}` : defaultError;
                break;
            }
            default:
                message = this.statusMessage;
                break;
        }

        metaMessageElement.textContent = message;
    }

    /**
     * Updates file size display. Hides file size when status is error (shows only error message).
     * @returns {void}
     */
    #updateFileSize() {
        const fileSizeElement = this.shadowRoot?.querySelector('#fileSize');
        if (!fileSizeElement) { return; }

        if (this.status === 'error') {
            fileSizeElement.textContent = '';
        } else {
            fileSizeElement.textContent = this.#formatFileSize(this.#file?.size);
        }
    }

    /**
     * Keeps the remove button's accessible name tied to the current file.
     * @returns {void}
     */
    #updateRemoveButtonAccessibleName() {
        const removeButton = this.shadowRoot?.querySelector('#removeButton');
        if (!removeButton) { return; }

        const fileName = this.#file?.name || this.itemId || 'file';
        removeButton.setAttribute('aria-label', `Remove ${fileName}`);
    }
}

customElements.define('neptune-input-file-item', InputFileItem);
export default InputFileItem;
